import dayjs from "dayjs";
import { ReactNode } from "react";
import { CancellationIcon, ReissueIcon } from "src/assets/Icons";
import { GroupOptionType, BaseFilter } from "src/constants/filters";
import { compactNumber, formatTimeFromUnit } from "src/utility";
import i18n from "src/i18n";
import { PASSENGER_CLASSES } from "./SearchPage";

const getLayoverAirports = (segment: any): string[] =>
  (segment as SegmentType | undefined)?.layovers?.map(({ city, airport }) => `${city} (${airport})`) ?? [];

const getSegmentFlightValues =
  (field: keyof FlightType) =>
  (segment: any): (string | number)[] => [
    ...new Set(((segment as SegmentType | undefined)?.flights ?? []).map((flight) => (flight?.[field] as any) ?? "")),
  ];

const STATIC_ONEWAY_FILTERS = [
  {
    get title() {
      return i18n.t("Flights.listing.departureTime");
    },
    op: "between",
    key: "departure",
    component: "TimeSegments",
    category: "number",
    path: ["departure"],
    transform: (value) => dayjs(value).hour(),
  },
  {
    get title() {
      return i18n.t("Flights.listing.arrivalTime");
    },
    op: "between",
    key: "arrival",
    category: "number",
    component: "TimeSegments",
    path: ["arrival"],
    transform: (value) => dayjs(value).hour(),
  },
] as const satisfies BaseFilter[];

const STATIC_TWOWAY_FILTERS = [
  {
    get title() {
      return i18n.t("Flights.listing.returnArrivalTime");
    },
    op: "between",
    key: "returnArrival",
    category: "number",
    component: "TimeSegments",
    path: ["arrival"],
    transform: (value) => dayjs(value).hour(),
  },
  {
    get title() {
      return i18n.t("Flights.listing.returnDepartureTime");
    },
    op: "between",
    key: "returnDeparture",
    component: "TimeSegments",
    category: "number",
    path: ["segments", 1, "departure"],
    transform: (value) => dayjs(value).hour(),
  },
] as const satisfies BaseFilter[];

const DYNAMIC_TWOWAY_FILTERS = [
  {
    op: "==",
    key: "returnStops",
    category: "number",
    path: ["segments", 1, "stops"],
    get title() {
      return i18n.t("Flights.listing.returnStops");
    },
    component: "checkbox",
    label: "stop",
    labelTransform: (value: number) =>
      value === 0
        ? i18n.t("Flights.listing.direct")
        : value == 1
          ? i18n.t("Flights.listing.oneStop")
          : i18n.t("Flights.listing.multipleStops", { count: value }),
  },
  {
    op: "==",
    key: "returnLayovers",
    category: "string|number[]",
    path: ["segments", 1],
    transform: getLayoverAirports,
    get title() {
      return i18n.t("Flights.listing.returnLayoverAirports");
    },
    component: "checkbox",
    labelTransform: (value) => value,
  },
  {
    key: "returnCabinClass",
    category: "string|number[]",
    path: ["segments", 1],
    transform: getSegmentFlightValues("cabin"),
    op: "==",
    get title() {
      return i18n.t("Flights.listing.returnCabin");
    },
    component: "checkbox",
    labelTransform: (val) => PASSENGER_CLASSES?.find((item) => item.key === Number(val))?.label,
  },
] as const satisfies BaseFilter[];

const DYNAMIC_ONEWAY_FILTERS = [
  {
    key: "price",
    category: "number",
    path: ["price", "total"],
    op: "between",
    component: "range",
    get title() {
      return i18n.t("Flights.listing.price");
    },
    dependencies: [{ key: "currency", path: ["price", "currency"] }],
    labelTransform: (val, { currency }) => `${compactNumber(val)} ${currency}`,
  },
  {
    op: "between",
    key: "duration",
    category: "number",
    path: ["totalDurationMinutes"],
    component: "range",
    get title() {
      return i18n.t("Flights.listing.flightDuration");
    },
    labelTransform: (value: number) => formatTimeFromUnit(value, "m"),
  },
  {
    op: "==",
    key: "stops",
    category: "number",
    path: ["segments", 0, "stops"],
    get title() {
      return i18n.t("Flights.listing.stops");
    },
    component: "checkbox",
    label: "stop",
    labelTransform: (value: number) =>
      value == 0
        ? i18n.t("Flights.listing.direct")
        : value == 1
          ? i18n.t("Flights.listing.oneStop")
          : i18n.t("Flights.listing.multipleStops", { count: value }),
  },
  {
    op: "==",
    key: "layovers",
    category: "string|number[]",
    path: ["segments", 0],
    transform: getLayoverAirports,
    get title() {
      return i18n.t("Flights.listing.layoverAirports");
    },
    component: "checkbox",
    labelTransform: (value) => value,
  },
  {
    key: "airline",
    category: "string",
    op: "==",
    path: ["segments", 0, "flights", 0, "airline"],
    get title() {
      return i18n.t("Flights.listing.airlines");
    },
    component: "checkbox",
    labelTransform: (value) => value,
  },
  {
    key: "isRefundable",
    category: "boolean",
    path: ["isRefundable"],
    op: "==",
    get title() {
      return i18n.t("Flights.listing.refundability");
    },
    component: "checkbox",
    labelTransform: (value: boolean) => (value ? i18n.t("Flights.listing.refundable") : i18n.t("Flights.listing.nonRefundable")),
  },
  {
    key: "cabinClass",
    category: "string|number[]",
    path: ["segments", 0],
    transform: getSegmentFlightValues("cabin"),
    op: "==",
    get title() {
      return i18n.t("Flights.listing.departureCabin");
    },
    component: "checkbox",
    labelTransform: (val) => PASSENGER_CLASSES?.find((item) => item.key === Number(val))?.label,
  },
   {
    key: "supplierFareClass",
    category: "string|number[]",
    path: ["segments", 0],
    transform: getSegmentFlightValues("supplierFareClass"),
    op: "==",
    get title() {
      return "Supplier Fare Class"
    },
    component: "checkbox",
    labelTransform:(val)=> !val ? "N/A" : val
  },
] as const satisfies BaseFilter[];

const GROUP_OPTIONS = [
  { key: "stops", category: "number", path: ["segments", 0, "stops"] },
  { key: "returnStops", category: "number", path: ["segments", 1, "stops"] },
  { key: "layovers", category: "string|number[]", path: ["segments", 0], transform: getLayoverAirports },
  { key: "returnLayovers", category: "string|number[]", path: ["segments", 1], transform: getLayoverAirports },
  { key: "airline", category: "string", path: ["segments", 0, "flights", 0, "airline"] },
  { key: "duration", category: "number", path: ["totalDurationMinutes"] },
  { key: "price", category: "number", path: ["price", "total"] },
  { key: "isLcc", category: "boolean", path: ["isLcc"] },
  { key: "isRefundable", category: "boolean", path: ["isRefundable"] },
  { key: "departure", category: "date", path: ["departure"] },
  { key: "cabinClass", category: "string|number[]", path: ["segments", 0], transform: getSegmentFlightValues("cabin") },
  { key: "returnCabinClass", category: "string|number[]", path: ["segments", 1], transform: getSegmentFlightValues("cabin") },
  {key:"supplierFareClass",category:"string|number[]",path:["segments",0],transform:getSegmentFlightValues("supplierFareClass")},
  {key:"returnSupplierFareClass",category:"string|number[]",path:["segments",1],transform:getSegmentFlightValues("supplierFareClass")},
] as const satisfies GroupOptionType[];

const ALL_FILTERS = [...DYNAMIC_ONEWAY_FILTERS, ...DYNAMIC_TWOWAY_FILTERS, ...STATIC_ONEWAY_FILTERS, ...STATIC_TWOWAY_FILTERS];

export type JourneyFilterKeys =
  | (typeof STATIC_ONEWAY_FILTERS)[number]["key"]
  | (typeof STATIC_TWOWAY_FILTERS)[number]["key"]
  | (typeof DYNAMIC_TWOWAY_FILTERS)[number]["key"]
  | (typeof DYNAMIC_ONEWAY_FILTERS)[number]["key"];

export type DataGroupType = Map<(typeof GROUP_OPTIONS)[number]["key"], Map<any, JourneyType[]>>;

const ONEWAY_SORTERS: BaseFilter[] = [
  {
    key: "price",
    category: "number",
    path: ["price", "total"],
    get title() {
      return i18n.t("Flights.listing.cheapest");
    },
    component: "sorterButton",
    op: "==",
  },
  {
    key: "duration",
    op: "==",
    category: "number",
    path: ["segments", 0, "durationMinutes"],
    get title() {
      return i18n.t("Flights.listing.fastest");
    },
    component: "sorterButton",
  },
  {
    key: "departure",
    category: "date",
    path: ["departure"],
    get title() {
      return i18n.t("Flights.listing.earliest");
    },
    component: "sorterButton",
    op: "==",
  },
];

const INITIAL_SORTER_VALUE = {
  price: true,
};

export const ALL_SORTERS = [...ONEWAY_SORTERS];

export {
  INITIAL_SORTER_VALUE,
  GROUP_OPTIONS,
  DYNAMIC_TWOWAY_FILTERS,
  DYNAMIC_ONEWAY_FILTERS,
  STATIC_ONEWAY_FILTERS,
  STATIC_TWOWAY_FILTERS,
  ONEWAY_SORTERS,
  ALL_FILTERS,
};

type FlightResponseType = {
  journeys: JourneyType[];
  session: SearchSessionType;
};

export type MiniRule = {
  from: string;
  to: string;
  details: string;
  journeyPoints: string;
  type: string;
  unit: string;
};
type JourneyType = {
  journeyId: string;
  journeyType: number;
  totalDurationMinutes: number;
  departure: string;
  arrival: string;
  miniRules: MiniRule[][];

  price: Price;
  originalPrice: Price;

  validatingAirline: string;
  isRefundable: boolean;
  isLcc: boolean;

  segments: SegmentType[];
};

type Price = {
  currency: string;
  base: number;
  tax: number;
  total: number;
  priceInPoints: number;
};

type SegmentType = {
  direction: number;
  origin: string;
  destination: string;

  departure: string;
  arrival: string;

  durationMinutes: number;
  stops: number;

  flights: FlightType[];
  layovers: LayoverType[];
};

type FlightType = {
  flightNumber: string;
  airline: string;
  airlineCode: string;
  airlineImage: string;
  aircraft: AircraftType;
  origin: AirportType;
  destination: AirportType;
  departure: string;
  arrival: string;
  durationMinutes: number;
  cabin: number;
  supplierFareClass: string;
  checkedBaggage: string;
  carryOn: string;
  seatsAvailable: number;
};

type AircraftType = {
  iataCode: string;
  aircraftName: string;
};

type AirportType = {
  code: string;
  name: string;
  terminal: string;
  cityName: string;
  countryName: string;
};

type LayoverType = {
  airport: string;
  durationMinutes: number;
  airportName: string;
  city: string;
};

type SearchSessionType = {
  startedAt: string;
  expiresAt: string;
};
type SorterType =
  | {
      key: string;
      acs: boolean;
    }
  | {};

export type JourneyFilterType = Partial<Record<JourneyFilterKeys, any>>;
type PolicyType = {
  type: "cancellation" | "reissue";
  bannerColor: string;
  bgColor: string;
  label: string;
  iconBgColor: string;
  icon: ReactNode;
};

const POLICIES = [
  {
    type: "cancellation",
    bannerColor: "var(--color-font-danger)",
    bgColor: "var(--color-background-danger)",
    get label() {
      return i18n.t("Flights.listing.cancellation");
    },
    iconBgColor: "var(--color-background-danger)",
    icon: <CancellationIcon className="text-font-danger" />,
  },
  {
    type: "reissue",
    bannerColor: "var(--color-font-informative)",
    bgColor: "var(--color-background-informative)",
    get label() {
      return i18n.t("Flights.listing.reissueChange");
    },
    iconBgColor: "var(--color-background-informative)",
    icon: <ReissueIcon className="text-font-informative" />,
  },
] as const satisfies PolicyType[];

type Policy = {
  type: string;
  rules: Array<{
    from: string | null;
    to: string | null;
    unit: string | null;
    details: string;
  }>;
};

type MappedJourney = {
  key: string;
  policies: Policy[];
};

const SINGLE_JOURNEY_CARD_HEIGHT_DESKTOP = 270;
const RETURN_JOURNEY_CARD_HEIGHT_DESKTOP = 370;
const SINGLE_JOURNEY_CARD_HEIGHT_MOBILE = 310;
const RETURN_JOURNEY_CARD_HEIGHT_MOBILE = 430;

const JOURNEY_LIST_WINDOW_SIZE = 10;
const JOURNEY_CARD_LISTING_GAP_DESKTOP = 15;
const JOURNEY_CARD_LISTING_GAP_MOBILE = 15;

export {
  SINGLE_JOURNEY_CARD_HEIGHT_DESKTOP,
  RETURN_JOURNEY_CARD_HEIGHT_DESKTOP,
  SINGLE_JOURNEY_CARD_HEIGHT_MOBILE,
  RETURN_JOURNEY_CARD_HEIGHT_MOBILE,
  JOURNEY_LIST_WINDOW_SIZE,
  JOURNEY_CARD_LISTING_GAP_DESKTOP,
  JOURNEY_CARD_LISTING_GAP_MOBILE,
  POLICIES,
};
export type {
  FlightResponseType,
  JourneyType,
  Price,
  SegmentType,
  FlightType,
  AircraftType,
  AirportType,
  LayoverType,
  SearchSessionType,
  SorterType,
  PolicyType,
  Policy,
  MappedJourney,
};
